import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { C } from '../../constants';
import { isToday } from '../utils/dateUtils';

const STATUS_COLOR = { completed: '#059669', cancelled: '#DC2626', pending: '#D97706' };
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function CalendarView({ setPage }) {
  const [bookings, setBookings] = useState([]);
  const [cur, setCur] = useState(() => { const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() }; });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  const prev = () => setCur(c => c.month === 0 ? { year: c.year-1, month: 11 } : { ...c, month: c.month-1 });
  const next = () => setCur(c => c.month === 11 ? { year: c.year+1, month: 0 } : { ...c, month: c.month+1 });

  const daysInMonth = new Date(cur.year, cur.month+1, 0).getDate();
  const firstDay = new Date(cur.year, cur.month, 1).getDay();

  const byDate = {};
  bookings.forEach(b => { if (!b.date) return; if (!byDate[b.date]) byDate[b.date] = []; byDate[b.date].push(b); });

  const selStr = selected ? `${cur.year}-${String(cur.month+1).padStart(2,'0')}-${String(selected).padStart(2,'0')}` : null;
  const selBookings = selStr ? (byDate[selStr] || []) : [];

  return (
    <div className="admin-page" style={{ padding: '32px' }}>
      <h1 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:'2rem', fontWeight:700, color:'#0B1D3A', marginBottom:4 }}>Calendar</h1>
      <p style={{ color:'#8899AA', fontSize:'0.88rem', marginBottom:28 }}>Tap any date to see appointments.</p>

      <div className="admin-analytics-2" style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:20, alignItems:'flex-start' }}>

        {/* Calendar */}
        <div style={{ background:'white', borderRadius:10, padding:24, border:'1px solid rgba(26,68,128,0.08)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <button onClick={prev} style={{ width:36, height:36, borderRadius:8, border:'1px solid rgba(26,68,128,0.15)', background:'white', cursor:'pointer', fontSize:'1.1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
            <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:'1.3rem', fontWeight:600, color:'#0B1D3A' }}>{MONTHS[cur.month]} {cur.year}</h2>
            <button onClick={next} style={{ width:36, height:36, borderRadius:8, border:'1px solid rgba(26,68,128,0.15)', background:'white', cursor:'pointer', fontSize:'1.1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:4 }}>
            {DAYS.map(d => <div key={d} style={{ textAlign:'center', fontSize:'0.68rem', fontWeight:600, color:'#8899AA', padding:'6px 0', letterSpacing:'0.05em' }}>{d}</div>)}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
            {Array.from({ length: firstDay }).map((_,i) => <div key={'e'+i} />)}
            {Array.from({ length: daysInMonth }).map((_,i) => {
              const day = i+1;
              const ds = `${cur.year}-${String(cur.month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const items = byDate[ds] || [];
              const hasB = items.length > 0;
              const todayB = isToday(ds);
              const isSel = selected === day;
              return (
                <div key={day} onClick={() => setSelected(isSel ? null : day)} style={{
                  borderRadius:8, padding:'6px 2px', minHeight:52, cursor: hasB ? 'pointer' : 'default',
                  background: isSel ? C.navy : todayB ? 'rgba(200,169,110,0.1)' : hasB ? 'rgba(26,68,128,0.04)' : 'transparent',
                  border: `1px solid ${isSel ? C.navy : todayB ? 'rgba(200,169,110,0.4)' : hasB ? 'rgba(26,68,128,0.1)' : 'transparent'}`,
                  transition: 'all 0.15s',
                }}>
                  <div style={{ textAlign:'center', fontSize:'0.82rem', fontWeight: todayB ? 700 : 400, color: isSel ? 'white' : todayB ? C.gold : '#1C2B3A', marginBottom:3 }}>{day}</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:2, justifyContent:'center' }}>
                    {items.slice(0,3).map((b,idx) => (
                      <div key={idx} style={{ width:6, height:6, borderRadius:'50%', background: isSel ? 'rgba(255,255,255,0.7)' : STATUS_COLOR[b.status||'pending'] }} />
                    ))}
                    {items.length > 3 && <div style={{ fontSize:'0.55rem', color: isSel ? 'rgba(255,255,255,0.6)' : '#8899AA' }}>+{items.length-3}</div>}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display:'flex', gap:14, marginTop:16, paddingTop:14, borderTop:'1px solid rgba(26,68,128,0.06)', flexWrap:'wrap' }}>
            {[['#D97706','Pending'],['#059669','Completed'],['#DC2626','Cancelled']].map(([color,label]) => (
              <div key={label} style={{ display:'flex', alignItems:'center', gap:5 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:color }} />
                <span style={{ fontSize:'0.72rem', color:'#8899AA' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Side panel */}
        <div style={{ background:'white', borderRadius:10, padding:20, border:'1px solid rgba(26,68,128,0.08)', minHeight:200 }}>
          {!selected ? (
            <div style={{ textAlign:'center', padding:'32px 0', color:'#8899AA' }}>
              <div style={{ fontSize:'2rem', marginBottom:8 }}>📅</div>
              <p style={{ fontSize:'0.84rem' }}>Tap a date to view appointments</p>
            </div>
          ) : (
            <>
              <h3 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:'1.15rem', fontWeight:600, color:'#0B1D3A', marginBottom:3 }}>
                {MONTHS[cur.month]} {selected}
              </h3>
              <p style={{ fontSize:'0.76rem', color:'#8899AA', marginBottom:16 }}>{selBookings.length} appointment{selBookings.length !== 1 ? 's' : ''}</p>
              {selBookings.length === 0 ? (
                <p style={{ color:'#8899AA', fontSize:'0.84rem', textAlign:'center', padding:'20px 0' }}>No appointments.</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {selBookings.map(b => (
                    <div key={b.id} style={{ padding:'12px 14px', borderRadius:8, background:'#F4F7FB', border:'1px solid rgba(26,68,128,0.06)' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                        <span style={{ fontWeight:600, color:'#0B1D3A', fontSize:'0.88rem' }}>{b.name}</span>
                        <span style={{ fontSize:'0.68rem', padding:'2px 8px', borderRadius:12, background: STATUS_COLOR[b.status||'pending']+'18', color: STATUS_COLOR[b.status||'pending'], fontWeight:600, textTransform:'capitalize' }}>{b.status||'pending'}</span>
                      </div>
                      <div style={{ fontSize:'0.78rem', color:'#4A5568' }}>{b.service}</div>
                      <div style={{ fontSize:'0.74rem', color:'#8899AA' }}>{b.phone}</div>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setPage('appointments')} style={{ marginTop:14, width:'100%', padding:'10px', background:C.navy, color:'white', border:'none', borderRadius:6, cursor:'pointer', fontSize:'0.8rem', fontFamily:'Jost,sans-serif', fontWeight:500 }}>
                Manage →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
